/**
 * This should use the Data Management (Lexicon++) mechanics to do an event/property rename
 * Currently it is expected that from an app to call fetchDisplayNameData when fetching display names is necessary
 * Otherwise, display names will be mapped from mp.report.globals
 * TODO: In the near future as we bundle event dropdowns into a component, the registry will initialized as part of that
 */

import {getOwnAttr} from 'common/util';
import {eventPropertyDefinitionsStore, peoplePropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';

import {EVENTS, PROPERTIES} from './constants';

/**
 * Defensively access mp.report.globals
 */
function getMpReportGlobals() {
  // eslint-disable-next-line no-undef
  // eslint-disable-next-line local-rules/deprecated-member-access
  return (typeof mp === `object` && mp.report?.globals) || {};
}

export function getEventDisplayName(eventName: string): string {
  return (
    EVENTS[eventName] ||
    eventDefinitionsStore.value[eventName]?.displayName ||
    getMpReportGlobals().mapped_events?.[eventName] ||
    eventName
  );
}

export function getPropertyDisplayName(propertyName: string): string {
  return (
    getOwnAttr(eventPropertyDefinitionsStore.value, propertyName)?.displayName ||
    getOwnAttr(peoplePropertyDefinitionsStore.value, propertyName)?.displayName ||
    getOwnAttr(getMpReportGlobals().mapped_properties || {}, propertyName) ||
    getOwnAttr(PROPERTIES, propertyName) ||
    propertyName
  );
}

export type FetchDisplayNamesReturnType = [void, void, void];

/**
 * Privately scoped promise to ensure that we don't subscribe multiple times
 */
let _fetchDisplayNameDataPromise: Promise<FetchDisplayNamesReturnType> | null = null;

/**
 * Populate stores required for display name mappings.
 * Returns a promise that resolves once the store subscription is fired for each request.
 */
export function fetchDisplayNameData(projectId: number, workspaceId?: number): Promise<FetchDisplayNamesReturnType> {
  function promisifySubscription(store): Promise<void> {
    return new Promise((resolve) => {
      store.subscribe(() => resolve());
    });
  }

  if (_fetchDisplayNameDataPromise !== null) {
    return _fetchDisplayNameDataPromise;
  }

  eventPropertyDefinitionsStore.fetch({projectId, workspaceId, category: `event`});
  peoplePropertyDefinitionsStore.fetch({projectId, workspaceId, category: `user`});
  eventDefinitionsStore.fetch({projectId, workspaceId});

  const subscriptionPromise = Promise.all([
    promisifySubscription(eventDefinitionsStore),
    promisifySubscription(eventPropertyDefinitionsStore),
    promisifySubscription(peoplePropertyDefinitionsStore),
  ]);

  return (_fetchDisplayNameDataPromise = subscriptionPromise);
}
